import { Box, Text } from '@chakra-ui/react';
import SalesSectionCard from '../Sales/components/SalesSectionCard';

export const CHART_EXPLANATIONS = [
  {
    title: 'UA spend vs CAC by cohort',
    meaning:
      'This chart shows how CAC moves with monthly UA spend. Each selected cohort is one spend–CAC pair.',
    series: [
      'Filled dots are actual CAC: UA spend ÷ first-month users when both are positive, otherwise the sales CAC.',
      'Hollow dots are the CAC predicted for that cohort from a linear fit on all earlier cohorts. The first three cohorts have no hollow dot. Hiding a cohort hides its dot; it does not change other cohorts’ predictions.',
      'The solid blue line is the trend of actual CAC versus spend, fit only on the selected actual cohorts. Planned cohorts are left out. Linear, log, and power need 3 selected actuals; quadratic needs 4.',
      'The shaded bands are ±1σ (darker) and ±2σ (lighter) around that trend. σ is the square root of the residual sum of squares ÷ (n − number of fit parameters). Power uses a multiplicative band.',
      'Grey dashed lines are the other fit types on the same selected points. The red dashed line is the selected deal’s max CAC.',
    ],
    calculation: [
      'The hollow-dot forecast is a linear regression of CAC on spend using every earlier cohort with spend and CAC, whether or not it is selected.',
      'The blue trend is ordinary least squares on the selected actuals. Spend is scaled by $1m: linear CAC = a + b·s; log CAC = a + b·ln(s); power fits ln(CAC); quadratic CAC = a + b·s + c·s².',
      'Spend at max CAC is the smallest amount from $500k to $6m, in $5k steps, where the fitted CAC reaches the deal max CAC, or >$6m if it never does.',
    ],
  },
  {
    title: 'Cumulative ROAS by month on book',
    meaning:
      'This chart shows cumulative ROAS as a cohort ages, from MOB 0 (the first cohort month) through MOB 23.',
    series: [
      'The solid line is actual ROAS. Gross or net comes from Preferences. The latest cohort includes its open month.',
      'The dotted line is that cohort’s own sales prediction, continuing from the last actual point.',
      'The dashed line is the ex-ante forecast: first-month ROAS predicted from this cohort’s spend, then grown with the average path of other cohorts. It is not the dotted continuation.',
      'The light blue band is ±1σ / ±2σ around the ex-ante line.',
      'The red dashed step and diamonds are the deal ROAS covenant. The orange dashed line is the second-level breach. Scale % does not move these anchors.',
      'Scale multiplies the actual, dotted, and dashed cohort curves by the percent you type, after the forecast is calculated.',
    ],
    calculation: [
      'Ex-ante step 1 fits first-month ROAS against UA spend on earlier cohorts only, using this chart’s M1 fit, not the CAC fit. Linear, log, and power need 3 earlier cohorts; quadratic needs 4.',
      'Step 2 plugs this cohort’s spend into that curve to get predicted first-month ROAS.',
      'Step 3 grows that value with spend-weighted factors from every loaded cohort, including later ones: factor = Σ(spend × ROAS at the next month) ÷ Σ(spend × ROAS at this month). Ex-ante at month m = predicted first-month ROAS × the chain of factors through m.',
      'Selecting cohorts only shows or hides lines. Last 6, Last 12, All, and Clear do not refit the ex-ante curve.',
      'Band error = actual ÷ ex-ante − 1. σ is the sample standard deviation of those errors at that month. At least 3 cohorts are required; otherwise the last σ is carried forward. The band is ex-ante × (1 ± k·σ).',
    ],
  },
];

const MarketingChartNotes = () => {
  return (
    <SalesSectionCard title="How these charts are calculated">
      {CHART_EXPLANATIONS.map((section, index) => (
        <Box key={section.title} mt={index === 0 ? 4 : 8}>
          <Text fontWeight={700} fontSize="md" mb={2}>
            {section.title}
          </Text>
          <Text fontSize="sm" color="gray.700" mb={3}>
            {section.meaning}
          </Text>
          <Text fontSize="sm" fontWeight={600} mb={1}>
            What you are looking at
          </Text>
          {section.series.map((line) => (
            <Text key={line} fontSize="sm" color="gray.700" mb={1}>
              {line}
            </Text>
          ))}
          <Text fontSize="sm" fontWeight={600} mt={3} mb={1}>
            How it is calculated
          </Text>
          {section.calculation.map((line) => (
            <Text key={line} fontSize="sm" color="gray.700" mb={1}>
              {line}
            </Text>
          ))}
        </Box>
      ))}
    </SalesSectionCard>
  );
};

export default MarketingChartNotes;
